import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, makeStateKey, PLATFORM_ID, type StateKey, TransferState } from '@angular/core';
import { LRUCache } from 'lru-cache';
import type { Observable } from 'rxjs';
import { of, Subject } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { logError, SEVERITY } from '@core-mkt/shared/lib/logger';
import { EnvService } from '../env/env.service';
import type { WistiaApiData } from './wistia';

const WISTIA_API_TIMEOUT_MS = 2000;
const WISTIA_CACHE_MAX = 500;
const WISTIA_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// Exported so tests can clear between runs; not part of the service's public API.
// LRU values are shared by reference across requests/callers — never mutate a cached entry.
export const wistiaCache = new LRUCache<string, WistiaApiData>({
  max: WISTIA_CACHE_MAX,
  ttl: WISTIA_CACHE_TTL_MS,
});

const wistiaStateKey = (wistiaId: string): StateKey<WistiaApiData> => makeStateKey<WistiaApiData>(`wistia-${wistiaId}`);

/**
 * Minimum shape required by `parseStructureData` to build a valid VideoObject.
 * If a 200 response from Wistia ever omits these fields (contract change, partial response),
 * we treat it as a soft failure rather than caching the bad shape for 24h.
 *
 * Guards every field the schema builder reads. Without this, a partial payload
 * could pass the gate, reach `parseStructureData`, and emit ld+json with
 * `undefined` values — a quieter failure than throwing but worse for SEO.
 */
/**
 * Escapes characters that would prematurely terminate a `<script>` tag when
 * embedding JSON-LD. JSON.stringify is RFC 8259-compliant and does not escape
 * `<`, so a value containing `</script>` would close the script element early
 * in SSR HTML and expose the rest of the JSON to the HTML parser — an XSS
 * vector if any cached Wistia field carries the substring.
 *
 * `<` round-trips through JSON.parse cleanly, so Google's structured-data
 * crawler still sees the original character.
 */
const escapeJsonForScriptTag = (json: string): string =>
  json.replace(/</g, '\\u003C').replace(/>/g, '\\u003E').replace(/&/g, '\\u0026');

const isWellFormedWistiaData = (data: WistiaApiData | null | undefined): data is WistiaApiData =>
  !!data &&
  typeof data.name === 'string' &&
  typeof data.duration === 'number' &&
  typeof data.updated === 'string' &&
  !!data.thumbnail &&
  typeof data.thumbnail.url === 'string' &&
  data.thumbnail.url.length > 0 &&
  Array.isArray(data.assets) &&
  data.assets.some(
    (asset) =>
      (asset.type === 'IphoneVideoFile' || asset.type === 'VideoObject') &&
      typeof asset.url === 'string' &&
      asset.url.length > 0 &&
      typeof asset.contentType === 'string' &&
      asset.contentType.length > 0,
  );

/** Validates that a Wistia media ID is a safe alphanumeric hash (e.g. "abc123def"). */
export const isValidWistiaId = (id: string): boolean => /^[a-z0-9]+$/i.test(id);

@Injectable({
  providedIn: 'root',
})
export class ServerWistiaService {
  readonly document = inject<Document>(DOCUMENT);
  private readonly env = inject(EnvService);
  readonly httpClient = inject(HttpClient);
  private readonly transferState = inject(TransferState);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Coalesces concurrent fetches for the same wistiaId into a single HTTP call.
   * Without this, N shortcodes for the same id on a page fan out to N parallel
   * outbound requests before the first one populates the LRU. Service is
   * per-request on the server (Angular Universal), so this Map is per-render.
   */
  private inFlightRequests = new Map<string, Observable<WistiaApiData | null>>();
  protected isValidWistiaId(id: string): boolean {
    return isValidWistiaId(id);
  }

  /**
   * Primary regex for escaped quotes from Craft CMS
   * Matches: [wistia id=\"abc123\" type=\"embed\" ...any params...]
   * Parameters (llm, autoplay, loop) can appear in any order after id and type
   * NOTE: No `g` flag — `g` makes .test() stateful. Use `new RegExp(source, 'g')` for matchAll.
   */
  regExp = /\[wistia id=\\?"([^"\\]+?)\\?" type=\\?"([^"\\]+?)\\?"([^\]]*?)\]/;

  /**
   * Alternative regex for non-escaped quotes (for direct testing)
   * Matches: [wistia id="abc123" type="embed" ...any params...]
   * Parameters (llm, autoplay, loop) can appear in any order after id and type
   */
  regExpAlt = /\[wistia id="([^"]+)" type="([^"]+)"([^\]]*?)\]/;

  wistiaObject: WistiaApiData;

  scriptLoadedSubject = new Subject();

  /**
   * escapeHtml escapes HTML special characters
   */
  private escapeHtml(text: string): string {
    const div = this.document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * extractParameter extracts a parameter value from shortcode params string
   * Supports both escaped (\") and non-escaped (") quotes
   * Parameters can appear in any order
   */
  private extractParameter(paramsString: string, paramName: string): string | undefined {
    const escapedPattern = new RegExp(`${paramName}=\\\\?"([^"\\\\]*?)\\\\?"`, 'i');
    const nonEscapedPattern = new RegExp(`${paramName}="([^"]*?)"`, 'i');

    const escapedMatch = paramsString.match(escapedPattern);
    if (escapedMatch) {
      return escapedMatch[1];
    }

    const nonEscapedMatch = paramsString.match(nonEscapedPattern);
    if (nonEscapedMatch) {
      return nonEscapedMatch[1];
    }

    return undefined;
  }

  /**
   * Build poster HTML for any non-autoplay video (popover or inline embed).
   * Renders a static div (NOT <wistia-player>) so player.js cannot discover it.
   * Uses data-wistia-popover for popovers (opens in overlay) and data-wistia-poster
   * for inline embeds (replaces div with real player on click).
   * Swatch background + CSS play button + optional LLM transcript for SEO.
   */
  private buildPosterHtml(wistiaId: string, isPopover: boolean, llmHtml: string = ''): string {
    const attr = isPopover ? 'data-wistia-popover' : 'data-wistia-poster';
    return `
      <style>
        [${attr}="${wistiaId}"]{background:center/cover no-repeat url('https://fast.wistia.com/embed/medias/${wistiaId}/swatch');aspect-ratio:16/9;width:100%;cursor:pointer;position:relative}
        [${attr}="${wistiaId}"]::before{content:"";position:absolute;inset:0;margin:auto;width:100px;height:70px;background:#0e6069c2;transition:width .3s ease,height .3s ease;z-index:1}
        [${attr}="${wistiaId}"]:hover::before{width:100%;height:100%}
        [${attr}="${wistiaId}"]::after{content:"";position:absolute;inset:0;margin:auto;width:36px;height:36px;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M8 5v14l11-7z'/%3E%3C/svg%3E") no-repeat center/contain;z-index:2}
        [${attr}="${wistiaId}"] .wistia-duration{position:absolute;left:50%;top:50%;transform:translate(-50%,24px);color:white;font-size:14px;font-weight:500;z-index:2;opacity:0;transition:opacity .2s ease;pointer-events:none}
        [${attr}="${wistiaId}"]:hover .wistia-duration{opacity:1}
      </style>
      <div id="${wistiaId}" class="wistia-vid relative" ${attr}="${wistiaId}"><span class="wistia-duration"></span>${llmHtml}</div>
    `;
  }

  /**
   * Builds an optimized 960x540 thumbnail URL from the Wistia API thumbnail URL.
   */
  protected buildSharpThumbnailUrl(thumbnailUrl: string): string {
    const params = 'image_crop_resized=960x540&image_quality=80';
    if (/image_crop_resized=\d+x\d+/.test(thumbnailUrl)) {
      return thumbnailUrl.replace(/image_crop_resized=\d+x\d+/, params);
    }
    return thumbnailUrl + (thumbnailUrl.includes('?') ? '&' : '?') + params;
  }

  /**
   * Fetches Wistia API data and upgrades a poster element's thumbnail + duration.
   * Shared by both wistiaParser (SSR/hydration) and initializePopovers (client).
   * The getWistiaData call is the same one used for SEO structured data.
   */
  protected setupPoster(el: HTMLElement, wistiaId: string): void {
    this.getWistiaData(wistiaId).subscribe((data) => {
      // null = fetch failed; keep blurry swatch as fallback
      if (!data) return;
      if (data.thumbnail?.url) {
        el.style.backgroundImage = `url('${this.buildSharpThumbnailUrl(data.thumbnail.url)}')`;
      }
      if (data.duration) {
        const mins = Math.floor(data.duration / 60);
        const secs = Math.floor(data.duration % 60);
        const durationSpan = el.querySelector('.wistia-duration');
        if (durationSpan) {
          durationSpan.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;
        }
      }
    });
  }

  parseDuration(duration: number) {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `PT${minutes}M${seconds}S`;
  }

  /**
   * parseStructureData parses wistia api data into data structure expected by google.
   *
   * Uses JSON.stringify (not template literal interpolation) so values containing
   * unescaped quotes, newlines, or backslashes — common in CMS-authored description
   * HTML — produce valid RFC 8259 JSON that strict structured-data crawlers accept.
   */
  parseStructureData(wistiaId: string) {
    const formattedDuration = this.parseDuration(this.wistiaObject.duration);
    // Falsy check so a null/undefined/empty description falls back to the brand default.
    // Without this, JSON.stringify omits the description key entirely and we cache + emit
    // a partial VideoObject. Authored Wistia copy (incl. short stubs) is preserved verbatim.
    const desc = this.wistiaObject.description || this.env.get.brandConfig.defaultVideoDescription;
    // Match the well-formed predicate in isWellFormedWistiaData so we never pick an asset
    // that's missing url or contentType — those fields are required to build contentUrl.
    const wistAsset = this.wistiaObject.assets.find(
      (x) =>
        (x.type === 'IphoneVideoFile' || x.type === 'VideoObject') &&
        typeof x.url === 'string' &&
        x.url.length > 0 &&
        typeof x.contentType === 'string' &&
        x.contentType.length > 0,
    );
    const contentType = wistAsset?.contentType.split('/')[1] ?? '';
    const contentUrl = `${wistAsset?.url.split('.bin')[0] ?? ''}.${contentType}`;

    // embedUrl per schema.org/VideoObject is a media-player URL, not the asset directory.
    // The previous implementation produced "http://embed.wistia.com/deliveries" (the asset
    // path with the filename stripped), which Google rejects as not pointing at a player.
    const embedUrl = `https://fast.wistia.net/embed/iframe/${wistiaId}`;

    // Shared @id with Aurora's runtime VideoObject (w-json-ld) so Google merges them as the
    // same entity instead of counting both as duplicate videos. Aurora injects schema only
    // when a real <wistia-player> instantiates (autoplay-in-viewport or click); below-fold
    // and popover videos never get Aurora schema, so our SSR copy is what guarantees coverage.
    // See EPS-3911 for the full duplication analysis.
    const schemaId = embedUrl;

    // regionsAllowed verified for all current brands (ace, aa, aarp, dec, ids, ins, mtg) —
    // every brand serves US-state-specific licensing/education content. Revisit if Aceable
    // expands internationally.
    const schema = {
      '@context': 'https://schema.org',
      '@id': schemaId,
      '@type': 'VideoObject',
      name: this.wistiaObject.name,
      description: desc,
      thumbnailUrl: [this.wistiaObject.thumbnail.url],
      duration: formattedDuration,
      uploadDate: this.wistiaObject.updated,
      contentUrl,
      embedUrl,
      regionsAllowed: 'US',
    };

    this.insertStructuredDataElement(escapeJsonForScriptTag(JSON.stringify(schema, null, 2)), wistiaId);
  }

  /**
   * insertStructuredDataElement inserts structured data into application <head>.
   * Skips when a script for this wistiaId already exists — prevents duplicates from
   * (a) multiple shortcodes per id within a render and (b) hydration re-running the
   * SSR redactor on the client.
   */
  insertStructuredDataElement(wistiaStructuredData: string, wistiaId?: string) {
    const { head } = this.document;
    if (wistiaId && head.querySelector(`script[data-wistia-id="${wistiaId}"]`)) {
      return;
    }
    const s = this.document.createElement('script');
    s.type = 'application/ld+json';
    s.text = wistiaStructuredData;
    s.onload = () => this.scriptLoadedSubject.next({ type: 'wistia', loaded: true });
    s.setAttribute('script-loader:wistia', '');
    if (wistiaId) {
      s.setAttribute('data-wistia-id', wistiaId);
    }
    head.appendChild(s);
  }

  /**
   * Inserts the media-specific Wistia embed script (fast.wistia.com/embed/{id}.js).
   * Required alongside player.js for Aurora to fully initialize a video.
   */
  insertWistiaEmbedScript(wistiaId: string): void {
    if (!this.isValidWistiaId(wistiaId)) return;
    const { head } = this.document;
    const src = `https://fast.wistia.com/embed/${wistiaId}.js`;
    if (head.querySelector(`script[src="${src}"]`)) {
      return;
    }
    const script = this.document.createElement('script');
    script.src = src;
    script.async = true;
    script.type = 'module';
    head.appendChild(script);
  }

  /**
   * insertWistiaPlayerScript inserts Aurora player.js into application <head>.
   * This single script handles all Wistia video loading — no per-video .jsonp needed.
   */
  insertWistiaPlayerScript() {
    const { head } = this.document;
    const existingScript = head.querySelector('script[src*="fast.wistia.com/player.js"]');
    if (existingScript) {
      return;
    }
    const script = this.document.createElement('script');
    script.src = 'https://fast.wistia.com/player.js';
    script.async = true;
    script.type = 'module';
    script.setAttribute('script-loader:wistia-player', '');
    head.appendChild(script);
  }

  /**
   * getWistiaData calls the Wistia API to pull video data for structured data injection.
   *
   * Resolution order:
   *  1. Process-level LRU (cross-request on the SSR Node process)
   *  2. TransferState (browser-only, populated by the SSR render that produced this HTML —
   *     eliminates the duplicate Wistia API call during hydration)
   *  3. HTTP fetch — bounded by a 2s timeout, populates LRU on success, and (on the server)
   *     publishes to TransferState so the client picks it up during hydration
   *
   * On failure (timeout, non-2xx, network) emits `null` so callers can skip schema injection
   * without surfacing the error — keeps the SSR critical path resilient to a Wistia outage
   * or stampede after a CF `/*` invalidation.
   */
  getWistiaData = (wistiaId: string): Observable<WistiaApiData | null> => {
    // Reject malformed ids before they pollute the cache, the URL, or the log stream.
    // Upstream callers like wistiaParser already validate, but insertWistiaCtaData
    // accepts CMS-provided ids unchecked beyond null/empty.
    if (!this.isValidWistiaId(wistiaId)) {
      return of(null);
    }

    const stateKey = wistiaStateKey(wistiaId);

    const cached = wistiaCache.get(wistiaId);
    if (cached) {
      // On the server, ensure the cached value is also handed off to the browser so it
      // doesn't refetch during hydration. (Cache-hit renders would otherwise skip
      // TransferState population and force the client to make its own call.)
      if (isPlatformServer(this.platformId)) {
        this.transferState.set(stateKey, cached);
      }
      return of(cached);
    }

    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(stateKey)) {
      const transferred = this.transferState.get<WistiaApiData | null>(stateKey, null);
      // Remove after read so TransferState doesn't grow on repeated lookups for the
      // same id within a single browser session — the LRU now owns subsequent reads.
      this.transferState.remove(stateKey);
      if (transferred) {
        wistiaCache.set(wistiaId, transferred);
        return of(transferred);
      }
    }

    // Concurrent calls for the same id share a single HTTP fetch. Without this,
    // a page with N shortcodes for one id fans out to N parallel outbound requests
    // before any of them lands in the LRU.
    const existing = this.inFlightRequests.get(wistiaId);
    if (existing) {
      return existing;
    }

    const url = `https://api.wistia.com/v1/medias/${wistiaId}.json`;
    const request$ = this.httpClient
      .get<WistiaApiData>(url, {
        headers: {
          Authorization: this.env.get.wistiaVerification,
        },
      })
      .pipe(
        timeout(WISTIA_API_TIMEOUT_MS),
        map((data): WistiaApiData | null => {
          // Reject malformed-but-200 responses before they poison the LRU for 24h.
          // parseStructureData would otherwise throw on `assets.find(...)` or `thumbnail.url`.
          if (!isWellFormedWistiaData(data)) {
            const platformTag = isPlatformBrowser(this.platformId) ? 'BROWSER' : 'SSR';
            logError({
              error: new Error('Wistia API returned a 200 response with missing fields'),
              message: 'WISTIA_API_MALFORMED',
              severity: SEVERITY.WARN,
              tags: ['WISTIA', platformTag],
              wistiaId,
            });
            return null;
          }
          return data;
        }),
        tap((data) => {
          if (!data) return;
          wistiaCache.set(wistiaId, data);
          if (isPlatformServer(this.platformId)) {
            this.transferState.set(stateKey, data);
          }
        }),
        catchError((error: unknown) => {
          // HttpClient surfaces HttpErrorResponse (extends HttpResponseBase, NOT Error).
          // Without this branch, the fallback `new Error(String(error))` produces the
          // useless "Error: [object Object]" with no status/url, defeating Coralogix triage.
          const errorContext: { err: Error; errorType: string; status?: number; url?: string } =
            error instanceof HttpErrorResponse
              ? {
                  err: Object.assign(new Error(error.message), { name: 'HttpErrorResponse' }),
                  errorType: 'HttpErrorResponse',
                  status: error.status,
                  url: error.url ?? undefined,
                }
              : error instanceof Error
              ? { err: error, errorType: error.name }
              : (() => {
                  const wrapped = new Error(String(error));
                  return { err: wrapped, errorType: wrapped.name };
                })();

          const platformTag = isPlatformBrowser(this.platformId) ? 'BROWSER' : 'SSR';
          logError({
            error: errorContext.err,
            message: 'WISTIA_API_FETCH',
            severity: SEVERITY.WARN,
            tags: ['WISTIA', platformTag],
            wistiaId,
            errorType: errorContext.errorType,
            status: errorContext.status,
            url: errorContext.url,
          });
          return of(null);
        }),
        // Clear the in-flight slot once the source completes (success or error path,
        // since catchError converts errors to a successful `of(null)` emission).
        // Future calls for this id will hit the LRU instead.
        finalize(() => {
          this.inFlightRequests.delete(wistiaId);
        }),
        // Replay the single emission to any subscriber that joins after the value lands.
        // refCount: false keeps the upstream subscription alive so finalize fires on
        // source-complete, not on subscriber-count-zero.
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.inFlightRequests.set(wistiaId, request$);
    return request$;
  };

  /**
   * wistiaParser converts [wistia ...] shortcodes into <wistia-player> web components.
   * Aurora handles poster display, lazy loading, autoplay, loop, and popover natively.
   *
   * Called from the redactorSanitizer.
   */
  wistiaParser = (content: string) => {
    if (content && typeof content === 'string' && typeof String.prototype.matchAll === 'function') {
      // Try primary regex (escaped quotes)
      let wistiaInstances = [...content.matchAll(new RegExp(this.regExp, 'g'))];

      // If no matches, try alternative regex (non-escaped quotes)
      if (!wistiaInstances.length) {
        wistiaInstances = [...content.matchAll(new RegExp(this.regExpAlt, 'g'))];
      }

      if (!wistiaInstances.length) {
        return content;
      }

      // Track processed IDs to avoid duplicates
      const processedIds = new Set<string>();

      // Sort instances to prioritize those with autoplay/loop parameters (process them first)
      wistiaInstances.sort((a, b) => {
        const aParams = a[3] || '';
        const bParams = b[3] || '';
        const aHasParams = aParams.includes('autoplay') || aParams.includes('loop');
        const bHasParams = bParams.includes('autoplay') || bParams.includes('loop');
        return bHasParams ? 1 : aHasParams ? -1 : 0;
      });

      wistiaInstances.forEach((instance) => {
        const wistiaId = instance[1];
        if (!this.isValidWistiaId(wistiaId)) {
          return;
        }
        const videoType = instance[2];
        const paramsString = instance[3] || '';

        // Extract parameters (can appear in any order)
        const llmText = this.extractParameter(paramsString, 'llm') || '';
        const autoplayParam = this.extractParameter(paramsString, 'autoplay');
        const loopParam = this.extractParameter(paramsString, 'loop');

        // Skip if this ID was already processed (prevents duplicate videos from CMS)
        if (processedIds.has(wistiaId)) {
          content = content.replace(instance[0], '');
          return;
        }
        processedIds.add(wistiaId);

        const isPopover = videoType === 'popover';
        const isAutoplay = autoplayParam === 'true';
        let playerHtml: string;

        if (isPopover || !isAutoplay) {
          // Static poster for popovers AND non-autoplay embeds — prevents player.js from loading on page load.
          // Scripts load on click via initializePopovers. LLM transcript is included as hidden text for SEO.
          const escapedLlm = llmText ? this.escapeHtml(llmText) : '';
          const llmHtml = llmText
            ? `<div class="wistia_preload_transcript_outer_wrapper" style="width:100%;height:0;overflow:hidden;position:absolute;top:0;left:0"><div class="wistia_preload_transcript_inner_wrapper"><p class="wistia_preload_transcript_text" aria-hidden="true" tabindex="-1" style="text-align:justify;font-size:5px!important">${escapedLlm}</p></div></div>`
            : '';
          playerHtml = this.buildPosterHtml(wistiaId, isPopover, llmHtml);
        } else {
          // Autoplay videos render as deferred divs — NOT <wistia-player> elements.
          // player.js auto-discovers all <wistia-player> elements on load, and an
          // autoplay+muted player pollutes Wistia's global muted state, causing
          // subsequent user-initiated videos to start muted. The real <wistia-player>
          // is created dynamically by initializePopovers when the IO fires.
          const loopAttr = loopParam === 'true' ? ' data-wistia-loop="true"' : '';
          playerHtml = `
            <style>
              [data-wistia-autoplay="${wistiaId}"]{background:center/cover no-repeat url('https://fast.wistia.com/embed/medias/${wistiaId}/swatch');aspect-ratio:16/9;width:100%;position:relative;filter:blur(5px)}
            </style>
            <div id="${wistiaId}" class="wistia-vid relative" data-wistia-autoplay="${wistiaId}"${loopAttr}></div>
          `;
        }

        content = content.replace(instance[0], playerHtml);

        // Scripts are NOT inserted here — deferred to initializePopovers (IntersectionObserver)
        // to avoid loading ~500 KB of Wistia assets on page load. The swatch CSS placeholder
        // renders via :not(:defined) without any JS.

        // Fetch structured data for SEO. On null (timeout / API error / malformed payload),
        // schema is omitted for this video — page still renders, no ld+json injected.
        this.getWistiaData(wistiaId).subscribe((data) => {
          this.injectStructuredDataSafely(data, wistiaId);
        });
      });
    }
    return content;
  };

  /**
   * insertWistiaCtaData fetches Wistia data and injects structured video data into head for SEO
   */
  insertWistiaCtaData(wistiaId: string) {
    if (wistiaId === null || wistiaId === '') {
      return;
    }
    this.getWistiaData(wistiaId).subscribe((data) => {
      this.injectStructuredDataSafely(data, wistiaId);
    });
  }

  /**
   * Defensive wrapper: assigns the Wistia data and runs `parseStructureData` inside a try/catch.
   * `parseStructureData` accesses fields like `assets.find(...)` and `thumbnail.url` without
   * null-checks, so a malformed payload that slipped past the cache shape check would otherwise
   * crash schema injection for the entire page. We log and swallow so the page still renders.
   */
  protected injectStructuredDataSafely(data: WistiaApiData | null, wistiaId: string): void {
    if (!data) return;
    this.wistiaObject = data;
    try {
      this.parseStructureData(wistiaId);
    } catch (error: unknown) {
      const platformTag = isPlatformBrowser(this.platformId) ? 'BROWSER' : 'SSR';
      logError({
        error: error instanceof Error ? error : new Error(String(error)),
        message: 'WISTIA_SCHEMA_INJECT',
        severity: SEVERITY.WARN,
        tags: ['WISTIA', platformTag],
        wistiaId,
      });
    }
  }

  /**
   * wistiaSingleInputParser wraps a plain Wistia ID in shortcode format if not already a shortcode
   */
  wistiaSingleInputParser = (input: string) => {
    if (this.regExp.test(input)) {
      return input;
    }
    return `[wistia id="${input}" type="popover"]`;
  };

  // Browser-only stubs — overridden in WistiaService for browser environment
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initializePopovers = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initializeCtaTriggers = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  openCtaPopover = (_trigger: Element, _wistiaId: string) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  loadVideoScripts = (_wistiaId: string, _userInitiated: boolean = false) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  hideSpinnerOnVideoReady(_wistiaId: string): void {}
}
